  (function () {
    const MAX_SWATCHES = 4;
    const WATCH_ROOT_SELECTOR = 'main, #MainContent, body';
    const SWATCH_CONTAINER_SELECTOR = '.productCard-swatches';
    const SWATCH_VIEW_BASE_URL = '/collections/all?view=swatches';
    // Cache of fetched ?view=swatches page HTML, keyed by page number. Persists across
    // initSwatches re-runs so the paginated catalog is only downloaded once per page view.
    const swatchPageCache = new Map();
    let mobileDrawerOwnerCounter = 0;

    let debounceTimer = null;

    function debounceInit() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => window.initSwatches(), 50);
    }

    function hookHistory(methodName) {
      const original = history[methodName];
      history[methodName] = function () {
        const ret = original.apply(this, arguments);
        window.dispatchEvent(new Event('locationchange'));
        return ret;
      };
    }

    if (!window.__agent84SwatchHistoryHooked) {
      hookHistory('pushState');
      hookHistory('replaceState');
      window.addEventListener('popstate', () => window.dispatchEvent(new Event('locationchange')));
      window.__agent84SwatchHistoryHooked = true;
    }

    let lastPageParam = new URLSearchParams(location.search).get('page') || '1';

    window.addEventListener('locationchange', () => {
      const nextPageParam = new URLSearchParams(location.search).get('page') || '1';

      if (nextPageParam !== lastPageParam) {
        lastPageParam = nextPageParam;
        debounceInit();
      }
    });

    const root = document.querySelector(WATCH_ROOT_SELECTOR) || document.body;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.(SWATCH_CONTAINER_SELECTOR) || node.querySelector?.(SWATCH_CONTAINER_SELECTOR)) {
            debounceInit();
            return;
          }
        }
      }
    });

    observer.observe(root, { childList: true, subtree: true });

    if (!window.__agent84SwatchBreakpointHooked) {
      window.matchMedia('(max-width: 768px)').addEventListener('change', debounceInit);
      window.__agent84SwatchBreakpointHooked = true;
    }

    function getSortLabelFromSwatchHtml(swatchHtml) {
      const html = String(swatchHtml || '');
      const classMatch = html.match(/\bswatch-color-([A-Za-z0-9_-]+)\b/);

      if (classMatch && classMatch[1]) {
        return classMatch[1].replace(/_/g, ' ').trim().toLowerCase();
      }

      const titleMatch = html.match(/title="([^"]+)"/i);
      return (titleMatch ? titleMatch[1] : '')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim()
        .toLowerCase();
    }

    function parseJsonAttribute(el, name, fallback) {
      try {
        const raw = el?.getAttribute(name);
        return raw ? JSON.parse(raw) : fallback;
      } catch (err) {
        console.warn(`Unable to parse ${name}`, err);
        return fallback;
      }
    }

    function escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function isSizeOptionName(name) {
      return String(name || '').trim().toLowerCase().includes('size');
    }

    function displayOptionText(value, optionName) {
      if (!isSizeOptionName(optionName)) return value;

      const normalized = String(value || '').trim().toLowerCase();
      const labels = {
        xsmall: 'XS',
        small: 'S',
        medium: 'M',
        large: 'L',
        'extra large': 'XL',
        'x-large': 'XL',
        xl: 'XL',
        xlarge: 'XL',
        'xx-large': 'XXL',
        xxl: 'XXL',
        xxlarge: 'XXL'
      };

      return labels[normalized] || value;
    }

    function variantOptions(variant) {
      if (Array.isArray(variant?.options)) return variant.options;

      return [variant?.option1, variant?.option2, variant?.option3].filter(option => option != null);
    }

    function extractSwatchClassName(value) {
      const className = String(value || '');
      const match = className.match(/\bswatch-color-[A-Za-z0-9_-]+\b/);
      return match ? match[0] : '';
    }

    function parseProductData(source) {
      const price = Number.parseInt(source.getAttribute('data-product-price') || '0', 10) || 0;
      const comparePrice = Number.parseInt(source.getAttribute('data-product-compare-price') || '0', 10) || 0;
      const hoverOptionIndexes = parseJsonAttribute(source, 'data-hover-option-indexes', []);

      return {
        id: source.getAttribute('data-related-product-id') || source.getAttribute('data-selected-product-id') || source.getAttribute('data-product-id') || '',
        url: source.getAttribute('data-swatch-url') || source.getAttribute('data-product-url') || source.getAttribute('href') || '',
        title: source.getAttribute('data-product-title') || source.getAttribute('title') || '',
        price,
        comparePrice,
        priceVaries: (source.getAttribute('data-product-price-varies') || '').toLowerCase() === 'true',
        imageFirst: source.getAttribute('data-swatch-img-first') || '',
        imageSecond: source.getAttribute('data-swatch-img-second') || '',
        images: (source.getAttribute('data-swatch-images') || '')
          .split('||')
          .map(url => url.trim())
          .filter(Boolean),
        options: parseJsonAttribute(source, 'data-product-options', []),
        variants: parseJsonAttribute(source, 'data-product-variants', []),
        hoverOptionIndexes: (Array.isArray(hoverOptionIndexes) ? hoverOptionIndexes : [])
          .map(index => Number.parseInt(index, 10))
          .filter(index => Number.isFinite(index) && index >= 0),
        sizeOptionIndex: Number.parseInt(source.getAttribute('data-size-option-index') || '-1', 10),
        swatchClass: source.getAttribute('data-selected-swatch-class') || extractSwatchClassName(source.className)
      };
    }

    function updateImage(img, src, alt) {
      if (!img || !src) return;

      img.removeAttribute('srcset');
      img.removeAttribute('data-srcset');
      img.src = src;
      img.setAttribute('src', src);

      if (alt) {
        img.alt = alt;
      }
    }

    function updateCardContent(card, productData) {
      if (!card) return;

      if (productData.url) {
        card.querySelectorAll('.grid-product__link').forEach(link => {
          link.href = productData.url;
        });
      }

      const title = card.querySelector('.grid-product__title');
      if (title && productData.title) {
        title.textContent = productData.title;
      }

      const price = card.querySelector('.grid-product__price');
      if (price && window.theme?.Currency?.formatMoney) {
        const formattedPrice = theme.Currency.formatMoney(productData.price, theme.settings.moneyFormat);
        const formattedCompare = theme.Currency.formatMoney(productData.comparePrice, theme.settings.moneyFormat);
        const priceHtml = productData.priceVaries && theme.strings?.productFrom
          ? `${theme.strings.productFrom}${formattedPrice}`
          : formattedPrice;

        price.innerHTML = priceHtml;

        if (productData.comparePrice > productData.price) {
          price.insertAdjacentHTML('beforeend', `
            <span class="visually-hidden">${escapeHtml(theme.strings?.regularPrice || 'Regular price')}</span>
            <span class="grid-product__price--original">${formattedCompare}</span>
            <span class="visually-hidden">${escapeHtml(theme.strings?.salePrice || 'Sale price')}</span>
          `);
        }
      }

      const primaryImage = card.querySelector('.grid__image-ratio img.image-element, .image-wrap img.image-element');
      updateImage(primaryImage, productData.imageFirst, productData.title);

      const secondaryWrap = card.querySelector('.grid-product__secondary-image');
      const secondaryImage = secondaryWrap?.querySelector('img');
      if (secondaryWrap) {
        secondaryWrap.classList.toggle('hide', !productData.imageSecond);
      }
      updateImage(secondaryImage, productData.imageSecond, productData.title);

      updateSwipeTrack(card, productData);
    }

    // Rebuild the mobile swipe track (collection-page cards) so every slide shows the
    // newly selected colour's media instead of only the first image.
    function updateSwipeTrack(card, productData) {
      const swipe = card.querySelector('[data-product-swipe]');
      if (!swipe) return;

      const track = swipe.querySelector('[data-product-swipe-track]');
      if (!track) return;

      let urls = Array.isArray(productData.images) ? productData.images : [];
      if (!urls.length) {
        urls = [productData.imageFirst, productData.imageSecond].filter(Boolean);
      }
      if (!urls.length) return;

      const slides = Array.from(track.querySelectorAll('[data-product-swipe-slide]'));
      if (!slides.length) return;

      // The new colour may have more slides than the original product — clone the first
      // slide as a template so the track always matches the new media count.
      while (slides.length < urls.length) {
        const clone = slides[0].cloneNode(true);
        track.appendChild(clone);
        slides.push(clone);
      }

      slides.forEach((slide, index) => {
        const hasImage = index < urls.length;
        slide.classList.toggle('hide', !hasImage);
        if (!hasImage) return;

        updateImage(slide.querySelector('img'), urls[index], productData.title);
        const link = slide.querySelector('a');
        if (link && productData.url) {
          link.href = productData.url;
        }
      });

      // Rebuild the pagination dots to match the new slide count.
      const dotsWrap = swipe.querySelector('[data-product-swipe-dots]');
      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        if (urls.length > 1) {
          for (let index = 0; index < urls.length; index++) {
            const dot = document.createElement('span');
            dot.className = 'grid-product__swipe-dot' + (index === 0 ? ' is-active' : '');
            dotsWrap.appendChild(dot);
          }
        }
      }

      // Snap back to the first slide of the new colour.
      track.scrollLeft = 0;
    }

    function getHoverOptionIndexes(productData) {
      if (Array.isArray(productData.hoverOptionIndexes) && productData.hoverOptionIndexes.length) {
        return productData.hoverOptionIndexes;
      }

      return productData.sizeOptionIndex >= 0 ? [productData.sizeOptionIndex] : [];
    }

    function parseSelectedOptions(container) {
      try {
        const raw = container?.getAttribute('data-hover-selected-options');
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        console.warn('Unable to parse hover selected options', err);
        return [];
      }
    }

    function setSelectedOptions(container, selectedOptions) {
      container.setAttribute('data-hover-selected-options', JSON.stringify(selectedOptions || []));
    }

    function variantMatchesSelectedOptions(variant, selectedOptions, skipIndex) {
      const options = variantOptions(variant);

      return selectedOptions.every((value, index) => {
        if (index === skipIndex || value == null || value === '') return true;
        return options[index] === value;
      });
    }

    function findVariantForSelectedOptions(productData, selectedOptions) {
      const variants = Array.isArray(productData.variants) ? productData.variants : [];
      const matching = variants.filter(variant => variantMatchesSelectedOptions(variant, selectedOptions));

      return matching.find(variant => variant.available) || matching[0] || null;
    }

    function getCardSwatchContainer(from) {
      const card = from?.closest?.('.grid-product');
      if (card) {
        return card.querySelector('.productCard-swatches');
      }

      return from?.closest?.('.productCard-swatches') || null;
    }

    function getCardVariantInner(from) {
      const card = from?.closest?.('.grid-product');
      if (isAlwaysVisibleSwatchCard(card)) {
        return card.querySelector('[data-card-size-overlay] .grid-product__hover-variants-inner');
      }

      const container = getCardSwatchContainer(from);
      if (!container) return null;

      return container.closest('.grid-product__hover-variants-inner')
        || container.closest('[data-card-variant-strip]')?.querySelector('.grid-product__hover-variants-inner');
    }

    function isAlwaysVisibleSwatchCard(card) {
      return !!card?.classList?.contains('grid-product--always-visible-swatches');
    }

    function getHoverSwatchContainer(button) {
      const fromCard = getCardSwatchContainer(button);
      if (fromCard) return fromCard;

      return button.closest('.grid-product__hover-variants')?.querySelector('.grid-product__hover-variants__swatches');
    }

    function getCurrentHoverProductData(container) {
      const activeSwatch = container.querySelector('.productSwatch a.active[data-related-product-id]');
      return parseProductData(activeSwatch || container);
    }

    // Labels are stamped on the options container by the Liquid so they stay locale-correct
    // across re-renders (reading the button text would break once it flips to "Sold Out").
    function getSingleAddLabels(optionRows) {
      return {
        add: optionRows?.getAttribute('data-add-to-cart-label') || 'Add to cart',
        soldOut: optionRows?.getAttribute('data-sold-out-label') || 'Sold Out'
      };
    }

    function singleAddButtonHtml(variant, labels) {
      const isAvailable = !!variant?.available;
      const unavailableClass = isAvailable ? '' : ' grid-product__hover-variant-size--unavailable';
      const disabledAttrs = isAvailable ? '' : ' disabled aria-disabled="true"';
      const text = isAvailable ? labels.add : labels.soldOut;

      return `
        <div class="grid-product__hover-variants__sizes grid-product__hover-variants__single" data-single-add>
          <button
            type="button"
            class="grid-product__hover-variant-size grid-product__hover-variant-add${unavailableClass}"
            data-hover-add-to-cart
            data-variant-id="${escapeHtml(variant.id)}"${disabledAttrs}>
            ${escapeHtml(text)}
          </button>
        </div>
      `;
    }

    function renderHoverOptionButtons(container, productData, selectedOptions = parseSelectedOptions(container)) {
      const wrapper = container.closest('.grid-product__hover-variants-inner') || getCardVariantInner(container);
      if (!wrapper) return;

      let optionRows = wrapper.querySelector('.grid-product__hover-variants__options');
      if (!optionRows) {
        const firstSizeRow = wrapper.querySelector('.grid-product__hover-variants__sizes');
        if (!firstSizeRow) return;

        optionRows = document.createElement('div');
        optionRows.className = 'grid-product__hover-variants__options';
        firstSizeRow.replaceWith(optionRows);
      }

      const indexes = getHoverOptionIndexes(productData);
      const variants = Array.isArray(productData.variants) ? productData.variants : [];

      if (!variants.length) {
        optionRows.innerHTML = '';
        return;
      }

      // "One sized" products quick-add via a single "Add to Cart" button. An option that
      // resolves to a single value across every variant (e.g. a lone "Default Title" or
      // "One Size") is not a real choice, so treat it as one-sized too — otherwise switching
      // swatch colour would render that value as a bogus size button. Re-rendered here so the
      // button stays pointed at the newly-selected colour's variant.
      const hasSelectableOption = indexes.some(index => {
        const values = new Set();
        for (const variant of variants) {
          const value = variantOptions(variant)[index];
          if (value != null && value !== '') values.add(value);
        }
        return values.size > 1;
      });

      if (!hasSelectableOption) {
        const singleVariant = variants.find(variant => variant.available) || variants[0] || null;
        optionRows.innerHTML = singleVariant
          ? singleAddButtonHtml(singleVariant, getSingleAddLabels(optionRows))
          : '';
        return;
      }

      const rows = [];

      for (const index of indexes) {
        const optionName = Array.isArray(productData.options) ? productData.options[index] : '';
        const seen = new Set();
        const buttons = [];

        for (const variant of variants) {
          const options = variantOptions(variant);
          const optionValue = options[index];
          if (!optionValue || seen.has(optionValue)) continue;

          seen.add(optionValue);

          const variantsForValue = variants.filter(candidate => variantOptions(candidate)[index] === optionValue);
          const chosenVariant = variantsForValue.find(candidate => {
            return candidate.available && variantMatchesSelectedOptions(candidate, selectedOptions, index);
          }) || variantsForValue.find(candidate => variantMatchesSelectedOptions(candidate, selectedOptions, index)) || variantsForValue.find(candidate => candidate.available) || variantsForValue[0];
          if (!chosenVariant) continue;

          const isAvailable = variantsForValue.some(candidate => {
            return candidate.available && variantMatchesSelectedOptions(candidate, selectedOptions, index);
          });
          const unavailableClass = isAvailable ? '' : ' grid-product__hover-variant-size--unavailable';
          const selectedClass = selectedOptions[index] === optionValue ? ' active' : '';
          const disabledAttrs = isAvailable ? '' : ' disabled aria-disabled="true"';
          const pressedAttr = selectedOptions[index] === optionValue ? ' aria-pressed="true"' : ' aria-pressed="false"';

          buttons.push(`
            <button
              type="button"
              class="grid-product__hover-variant-size${selectedClass}${unavailableClass}"
              data-variant-id="${escapeHtml(chosenVariant.id)}"
              data-option-index="${escapeHtml(index)}"
              data-option-value="${escapeHtml(optionValue)}"${pressedAttr}${disabledAttrs}>
              ${escapeHtml(displayOptionText(optionValue, optionName))}
            </button>
          `);
        }

        if (buttons.length) {
          rows.push(`<div class="grid-product__hover-variants__sizes" data-option-index="${escapeHtml(index)}">${buttons.join('')}</div>`);
        }
      }

      optionRows.innerHTML = rows.join('');
    }

    async function handleHoverOptionButton(button) {
      const container = getHoverSwatchContainer(button);
      if (!container || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      if (button.closest('.grid-product__hover-variants__sizes')?.classList.contains('is-loading')) return;

      const productData = getCurrentHoverProductData(container);
      const indexes = getHoverOptionIndexes(productData);
      const optionIndex = Number.parseInt(button.getAttribute('data-option-index') || '-1', 10);
      const optionValue = button.getAttribute('data-option-value') || '';
      if (!Number.isFinite(optionIndex) || optionIndex < 0 || !optionValue) return;

      let selectedOptions = parseSelectedOptions(container);
      selectedOptions[optionIndex] = optionValue;

      const selectedRowPosition = indexes.indexOf(optionIndex);
      if (selectedRowPosition < 0) return;

      const hasAllSelections = indexes.every(index => selectedOptions[index]);
      setSelectedOptions(container, selectedOptions);

      if (!hasAllSelections) {
        renderHoverOptionButtons(container, productData, selectedOptions);
        setHoverError(container, '');
        return;
      }

      const chosenVariant = findVariantForSelectedOptions(productData, selectedOptions);
      if (!chosenVariant?.available) {
        setHoverError(container, theme.strings?.unavailable || 'This item is unavailable.');
        renderHoverOptionButtons(container, productData, selectedOptions);
        return;
      }

      const success = await addHoverVariantToCart(button, chosenVariant.id);
      if (!success) return;

      selectedOptions = [];
      setSelectedOptions(container, selectedOptions);
      renderHoverOptionButtons(container, productData, selectedOptions);
      setHoverError(container, '');
    }

    function setHoverError(container, message) {
      const wrapper = container.closest('.grid-product__hover-variants-inner') || getCardVariantInner(container);
      if (!wrapper) return;

      let error = wrapper.querySelector('.grid-product__hover-variants__error');
      if (!error) {
        error = document.createElement('div');
        error.className = 'grid-product__hover-variants__error';
        error.setAttribute('role', 'status');
        wrapper.append(error);
      }

      error.textContent = message || '';
      error.hidden = !message;
    }

    function isMobileViewport() {
      return window.matchMedia('(max-width: 768px)').matches;
    }

    function ensureMobileDrawerOwner(card, drawer) {
      if (!card || !drawer) return;

      let ownerId = card.getAttribute('data-mobile-drawer-owner');
      if (!ownerId) {
        mobileDrawerOwnerCounter += 1;
        ownerId = `mobile-drawer-${mobileDrawerOwnerCounter}`;
        card.setAttribute('data-mobile-drawer-owner', ownerId);
      }

      if (!drawer.getAttribute('data-mobile-drawer-owner')) {
        drawer.setAttribute('data-mobile-drawer-owner', ownerId);
      }
    }

    function getMobileDrawer(card) {
      if (!card) return null;

      const inlineDrawer = card.querySelector('[data-mobile-product-drawer]');
      if (inlineDrawer) {
        ensureMobileDrawerOwner(card, inlineDrawer);
        return inlineDrawer;
      }

      const ownerId = card.getAttribute('data-mobile-drawer-owner');
      if (!ownerId || !window.CSS?.escape) return null;

      return document.querySelector(`[data-mobile-product-drawer][data-mobile-drawer-owner="${CSS.escape(ownerId)}"]`);
    }

    function mountMobileDrawerToBody(drawer) {
      if (!drawer || drawer.parentElement === document.body) return;
      document.body.appendChild(drawer);
    }

    function getDrawerSelectedOptions(drawer) {
      return parseJsonAttribute(drawer, 'data-selected-options', []);
    }

    function setDrawerSelectedOptions(drawer, selectedOptions) {
      drawer?.setAttribute('data-selected-options', JSON.stringify(selectedOptions || []));
    }

    function splitStyleLabel(title) {
      const value = String(title || '');
      const pieces = value.split('-').map(part => part.trim()).filter(Boolean);
      if (pieces.length < 2) return value;
      return pieces[pieces.length - 1];
    }

    function getCurrentHoverContainer(card) {
      return card?.querySelector('.productCard-swatches') || card?.querySelector('.grid-product__hover-variants__swatches');
    }

    function closeAllMobileCards(exceptCard) {
      document.querySelectorAll('.grid-product--mobile-quick-add.grid-product--mobile-active').forEach(card => {
        if (exceptCard && card === exceptCard) return;
        card.classList.remove('grid-product--mobile-active');
        card.querySelector('[data-mobile-hover-plus]')?.setAttribute('aria-expanded', 'false');
      });
    }

    function closeAllMobileDrawers(exceptDrawer) {
      document.querySelectorAll('[data-mobile-product-drawer].is-open').forEach(drawer => {
        if (exceptDrawer && drawer === exceptDrawer) return;
        drawer.classList.remove('is-open', 'grid-product__mobile-drawer--preparing');
        drawer.setAttribute('aria-hidden', 'true');
      });

      if (!exceptDrawer || !exceptDrawer.classList.contains('is-open')) {
        document.documentElement.classList.remove('js-mobile-card-drawer-open');
      }
    }

    function setMobileCardActive(card, isActive) {
      if (!card) return;
      if (isActive) {
        closeAllMobileCards(card);
        card.classList.add('grid-product--mobile-active');
      } else {
        card.classList.remove('grid-product--mobile-active');
      }

      card.querySelector('[data-mobile-hover-plus]')?.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    }

    function setMobileDrawerError(drawer, message) {
      const error = drawer?.querySelector('[data-mobile-drawer-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }

    function formatProductPriceHtml(productData) {
      if (!window.theme?.Currency?.formatMoney) return '';
      const moneyFormat =
        theme.settings?.moneyWithCurrencyFormat || theme.settings?.moneyFormat;
      const formattedPrice = theme.Currency.formatMoney(productData.price || 0, moneyFormat);
      if (productData.priceVaries && theme.strings?.productFrom) {
        return `${theme.strings.productFrom}${formattedPrice}`;
      }

      return formattedPrice;
    }

    function syncMobileDrawerData(drawer, productData) {
      if (!drawer || !productData) return;

      const variants = Array.isArray(productData.variants) ? productData.variants : [];
      const preferredVariant = variants.find(variant => variant.available) || variants[0] || null;
      const selectedOptions = preferredVariant ? variantOptions(preferredVariant) : [];

      drawer.setAttribute('data-product-id', productData.id || '');
      drawer.setAttribute('data-product-url', productData.url || '');
      drawer.setAttribute('data-product-title', productData.title || '');
      drawer.setAttribute('data-product-price', String(productData.price || 0));
      drawer.setAttribute('data-product-price-varies', String(!!productData.priceVaries));
      drawer.setAttribute('data-product-compare-price', String(productData.comparePrice || 0));
      drawer.setAttribute('data-product-options', JSON.stringify(productData.options || []));
      drawer.setAttribute('data-product-variants', JSON.stringify(productData.variants || []));
      drawer.setAttribute('data-hover-option-indexes', JSON.stringify(getHoverOptionIndexes(productData)));
      drawer.setAttribute('data-size-option-index', String(productData.sizeOptionIndex ?? -1));
      drawer.setAttribute('data-selected-variant-id', preferredVariant?.id ? String(preferredVariant.id) : '');
      drawer.setAttribute('data-selected-label', splitStyleLabel(productData.title || ''));
      drawer.setAttribute('data-selected-swatch-class', productData.swatchClass || '');
      setDrawerSelectedOptions(drawer, selectedOptions);

      const titleEl = drawer.querySelector('[data-mobile-drawer-title]');
      if (titleEl) titleEl.textContent = productData.title || '';

      const priceEl = drawer.querySelector('[data-mobile-drawer-price]');
      if (priceEl) {
        priceEl.innerHTML = formatProductPriceHtml(productData);
      }

      const selectedLabelEl = drawer.querySelector('[data-mobile-drawer-selected-label]');
      if (selectedLabelEl) {
        selectedLabelEl.textContent = splitStyleLabel(productData.title || '');
      }

      const swatchEl = drawer.querySelector('[data-mobile-drawer-selected-swatch]');
      if (swatchEl) {
        const baseClass = 'grid-product__mobile-drawer-selected-swatch';
        swatchEl.className = [baseClass, productData.swatchClass || ''].filter(Boolean).join(' ');
      }
    }

    function buildDrawerOptionRows(productData, selectedOptions) {
      const indexes = getHoverOptionIndexes(productData);
      const variants = Array.isArray(productData.variants) ? productData.variants : [];
      const options = Array.isArray(productData.options) ? productData.options : [];
      const rows = [];

      for (const index of indexes) {
        const optionName = options[index] || '';
        const seen = new Set();
        const buttons = [];

        for (const variant of variants) {
          const optionValue = variantOptions(variant)[index];
          if (!optionValue || seen.has(optionValue)) continue;
          seen.add(optionValue);

          const valueVariants = variants.filter(candidate => variantOptions(candidate)[index] === optionValue);
          const availableForSelection = valueVariants.some(candidate => {
            return candidate.available && variantMatchesSelectedOptions(candidate, selectedOptions, index);
          });
          const selectedClass = selectedOptions[index] === optionValue ? ' is-selected' : '';
          const unavailableClass = availableForSelection ? '' : ' grid-product__mobile-drawer-option-button--unavailable';
          const disabledAttrs = availableForSelection ? '' : ' disabled aria-disabled="true"';
          const pressedAttr = selectedOptions[index] === optionValue ? ' aria-pressed="true"' : ' aria-pressed="false"';

          buttons.push(`
            <button
              type="button"
              class="grid-product__mobile-drawer-option-button${selectedClass}${unavailableClass}"
              data-mobile-drawer-option-button
              data-option-index="${escapeHtml(index)}"
              data-option-value="${escapeHtml(optionValue)}"${pressedAttr}${disabledAttrs}>
              ${escapeHtml(displayOptionText(optionValue, optionName))}
            </button>
          `);
        }

        if (buttons.length) {
          const sizeGuideLink = isSizeOptionName(optionName)
            ? '<a href="javascript:void(0)" class="variant__label-info variant__label-info--size-guide">Size Guide</a>'
            : '';
          rows.push(`
            <div class="grid-product__mobile-drawer-option-row" data-mobile-drawer-option-row data-option-index="${escapeHtml(index)}">
              <div class="grid-product__mobile-drawer-option-header">
                <p class="grid-product__mobile-drawer-option-name">${escapeHtml(optionName)}</p>
                ${sizeGuideLink}
              </div>
              <div class="grid-product__mobile-drawer-option-values">${buttons.join('')}</div>
            </div>
          `);
        }
      }

      return rows.join('');
    }

    function updateMobileDrawerVariantState(drawer, productData, selectedOptions) {
      const submitButton = drawer?.querySelector('[data-mobile-drawer-submit]');
      if (!submitButton) return;

      const indexes = getHoverOptionIndexes(productData);
      const hasAllSelections = indexes.every(index => selectedOptions[index]);
      const selectedVariant = hasAllSelections ? findVariantForSelectedOptions(productData, selectedOptions) : null;
      const hasAnyAvailableVariant = Array.isArray(productData.variants)
        ? productData.variants.some(variant => variant.available)
        : false;
      const canSubmit = !!selectedVariant?.available;

      drawer.setAttribute('data-selected-variant-id', canSubmit ? String(selectedVariant.id) : '');
      submitButton.disabled = !canSubmit;
      submitButton.setAttribute('aria-disabled', canSubmit ? 'false' : 'true');

      const stockEl = drawer.querySelector('[data-mobile-drawer-stock]');
      if (stockEl) {
        stockEl.textContent = (canSubmit || (!hasAllSelections && hasAnyAvailableVariant)) ? 'In Stock' : 'Sold out';
      }
    }

    function renderMobileDrawerOptions(drawer, productData, selectedOptions = getDrawerSelectedOptions(drawer)) {
      if (!drawer || !productData) return;
      const optionsContainer = drawer.querySelector('[data-mobile-drawer-options]');
      if (!optionsContainer) return;

      optionsContainer.innerHTML = buildDrawerOptionRows(productData, selectedOptions);
      updateMobileDrawerVariantState(drawer, productData, selectedOptions);
    }

    function syncCardDrawerFromProduct(card, productData) {
      const drawer = getMobileDrawer(card);
      if (!drawer) return;
      syncMobileDrawerData(drawer, productData);
      renderMobileDrawerOptions(drawer, productData, getDrawerSelectedOptions(drawer));
      setMobileDrawerError(drawer, '');
    }

    function getDrawerProductData(drawer) {
      return parseProductData(drawer);
    }

    function openMobileDrawer(drawer) {
      if (!drawer) return;
      mountMobileDrawerToBody(drawer);
      closeAllMobileDrawers(drawer);
      drawer.classList.remove('is-open', 'grid-product__mobile-drawer--preparing');
      drawer.setAttribute('aria-hidden', 'true');

      drawer.classList.add('grid-product__mobile-drawer--preparing');
      void drawer.offsetHeight;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!drawer.isConnected) return;
          drawer.classList.remove('grid-product__mobile-drawer--preparing');
          drawer.classList.add('is-open');
          drawer.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('js-mobile-card-drawer-open');
        });
      });
    }

    function closeMobileDrawer(drawer) {
      if (!drawer) return;
      drawer.classList.remove('is-open', 'grid-product__mobile-drawer--preparing');
      drawer.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('[data-mobile-product-drawer].is-open')) {
        document.documentElement.classList.remove('js-mobile-card-drawer-open');
      }
    }

    async function addMobileDrawerVariantToCart(drawer) {
      const submitButton = drawer?.querySelector('[data-mobile-drawer-submit]');
      const variantId = drawer?.getAttribute('data-selected-variant-id');
      if (!submitButton || !variantId || submitButton.disabled) return;

      submitButton.classList.add('btn--loading');
      submitButton.disabled = true;
      setMobileDrawerError(drawer, '');

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        const data = await response.json();

        if (data.status === 422) {
          const message = typeof data.description === 'string' ? data.description : data.message;
          setMobileDrawerError(drawer, message || theme.strings?.unavailable || 'This item is unavailable.');
          dispatchHoverAjaxProductEvent('ajaxProduct:error', variantId, {
            errorMessage: data.description || data.message
          });
          return;
        }

        dispatchHoverAjaxProductEvent('ajaxProduct:added', variantId, {
          product: data,
          addToCartBtn: submitButton
        });
        closeMobileDrawer(drawer);
      } catch (err) {
        console.error(err);
        setMobileDrawerError(drawer, theme.strings?.unavailable || 'This item is unavailable.');
        dispatchHoverAjaxProductEvent('ajaxProduct:error', variantId, {
          errorMessage: err?.message || theme.strings?.unavailable || 'This item is unavailable.'
        });
      } finally {
        submitButton.classList.remove('btn--loading');
        const latestVariant = drawer?.getAttribute('data-selected-variant-id');
        submitButton.disabled = !latestVariant;
      }
    }

    function dispatchHoverAjaxProductEvent(eventName, variantId, detail) {
      const form = document.createElement('form');
      form.hidden = true;
      form.innerHTML = `<input type="hidden" name="id" value="${escapeHtml(variantId)}">`;
      document.body.append(form);

      form.dispatchEvent(new CustomEvent(eventName, {
        detail,
        bubbles: true
      }));

      form.remove();
    }

    function selectHoverSwatch(anchor) {
      const container = anchor.closest('.productCard-swatches');
      const card = anchor.closest('.grid-product');
      if (!container || !card) return;

      container.querySelectorAll('.productSwatch, .productSwatch a').forEach(el => {
        el.classList.remove('active');
      });

      anchor.classList.add('active');
      anchor.closest('.productSwatch')?.classList.add('active');

      const productData = parseProductData(anchor);
      container.setAttribute('data-selected-product-id', productData.id);
      container.setAttribute('data-product-url', productData.url);
      setSelectedOptions(container, []);

      const additionalLink = container.querySelector('.productCard-additional');
      if (additionalLink && productData.url) {
        additionalLink.href = productData.url;
      }

      updateCardContent(card, productData);
      renderHoverOptionButtons(container, productData, []);
      syncCardDrawerFromProduct(card, productData);
      setHoverError(container, '');
    }

    function resetSizeRowLoadingState(button) {
      const sizeRow = button.closest('.grid-product__hover-variants__sizes');
      const overlay = button.closest('[data-card-size-overlay]');

      button.classList.remove('btn--loading');
      sizeRow?.classList.remove('is-loading');
      sizeRow?.querySelectorAll('.grid-product__hover-variant-size').forEach(btn => {
        const isUnavailable = btn.classList.contains('grid-product__hover-variant-size--unavailable')
          || btn.getAttribute('aria-disabled') === 'true';
        btn.disabled = isUnavailable;
      });
      overlay?.removeAttribute('aria-busy');
    }

    async function addHoverVariantToCart(button, variantIdOverride) {
      const variantId = variantIdOverride || button.getAttribute('data-variant-id');
      const container = getHoverSwatchContainer(button);
      if (!variantId || !container || button.disabled || button.getAttribute('aria-disabled') === 'true') return false;
      if (button.classList.contains('btn--loading')) return false;

      const sizeRow = button.closest('.grid-product__hover-variants__sizes');
      const overlay = button.closest('[data-card-size-overlay]');

      button.classList.add('btn--loading');
      sizeRow?.classList.add('is-loading');
      sizeRow?.querySelectorAll('.grid-product__hover-variant-size').forEach(btn => {
        btn.disabled = true;
      });
      overlay?.setAttribute('aria-busy', 'true');
      setHoverError(container, '');

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        const data = await response.json();

        if (data.status === 422) {
          const message = typeof data.description === 'string' ? data.description : data.message;
          setHoverError(container, message || theme.strings?.unavailable || 'This item is unavailable.');
          dispatchHoverAjaxProductEvent('ajaxProduct:error', variantId, {
            errorMessage: data.description || data.message
          });
          return false;
        }

        dispatchHoverAjaxProductEvent('ajaxProduct:added', variantId, {
          product: data,
          addToCartBtn: button
        });
        return true;
      } catch (err) {
        console.error(err);
        setHoverError(container, theme.strings?.unavailable || 'This item is unavailable.');
        dispatchHoverAjaxProductEvent('ajaxProduct:error', variantId, {
          errorMessage: err?.message || theme.strings?.unavailable || 'This item is unavailable.'
        });
        return false;
      } finally {
        resetSizeRowLoadingState(button);
      }
    }

    // One-sized products: resolve the variant from the card's currently-selected swatch
    // (colour can be switched on the card) and add it straight to cart — no size drawer.
    async function addOneSizeVariantFromCard(card, button) {
      let variantId = '';

      const container = getCurrentHoverContainer(card);
      if (container) {
        const productData = getCurrentHoverProductData(container);
        const variants = Array.isArray(productData.variants) ? productData.variants : [];
        const variant = variants.find(candidate => candidate.available) || variants[0] || null;
        if (variant) variantId = String(variant.id);
      }

      if (!variantId) variantId = button.getAttribute('data-variant-id') || '';
      if (!variantId) return;

      await addHoverVariantToCart(button, variantId);
    }

    if (!window.__agent84HoverQuickAddHooked) {
      document.addEventListener('click', event => {
        const mobilePlus = event.target.closest('[data-mobile-hover-plus]');
        if (mobilePlus) {
          event.preventDefault();
          const card = mobilePlus.closest('.grid-product--mobile-quick-add');
          if (!card || !isMobileViewport()) return;

          if (mobilePlus.hasAttribute('data-mobile-one-size-add')) {
            addOneSizeVariantFromCard(card, mobilePlus);
            return;
          }

          if (isAlwaysVisibleSwatchCard(card)) {
            const hoverContainer = getCurrentHoverContainer(card);
            if (hoverContainer) {
              syncCardDrawerFromProduct(card, getCurrentHoverProductData(hoverContainer));
            }
            openMobileDrawer(getMobileDrawer(card));
            return;
          }

          const willBeActive = !card.classList.contains('grid-product--mobile-active');
          setMobileCardActive(card, willBeActive);
          if (!willBeActive) {
            closeMobileDrawer(getMobileDrawer(card));
          } else {
            const hoverContainer = getCurrentHoverContainer(card);
            if (hoverContainer) {
              syncCardDrawerFromProduct(card, getCurrentHoverProductData(hoverContainer));
            }
          }
          return;
        }

        const swatchAnchor = event.target.closest('.grid-product .productCard-swatches .productSwatch a[data-related-product-id]');
        if (swatchAnchor) {
          const navContainer = swatchAnchor.closest('.productCard-swatches');
          const navigateOnly =
            navContainer &&
            String(navContainer.getAttribute('data-swatch-click-navigate') || '').toLowerCase() === 'true';
          if (navigateOnly) {
            return;
          }
          event.preventDefault();
          const card = swatchAnchor.closest('.grid-product');
          if (card && isMobileViewport() && !isAlwaysVisibleSwatchCard(card)) {
            setMobileCardActive(card, true);
          }
          selectHoverSwatch(swatchAnchor);
          return;
        }

        const mobileAddButton = event.target.closest('[data-mobile-hover-add]');
        if (mobileAddButton) {
          event.preventDefault();
          const card = mobileAddButton.closest('.grid-product--mobile-quick-add');
          if (!card || !isMobileViewport()) return;
          setMobileCardActive(card, true);
          const hoverContainer = getCurrentHoverContainer(card);
          if (hoverContainer) {
            syncCardDrawerFromProduct(card, getCurrentHoverProductData(hoverContainer));
          }
          openMobileDrawer(getMobileDrawer(card));
          return;
        }

        const drawerOptionButton = event.target.closest('[data-mobile-drawer-option-button]');
        if (drawerOptionButton) {
          event.preventDefault();
          if (drawerOptionButton.disabled || drawerOptionButton.getAttribute('aria-disabled') === 'true') return;

          const drawer = drawerOptionButton.closest('[data-mobile-product-drawer]');
          const optionIndex = Number.parseInt(drawerOptionButton.getAttribute('data-option-index') || '-1', 10);
          const optionValue = drawerOptionButton.getAttribute('data-option-value') || '';
          if (!drawer || !Number.isFinite(optionIndex) || optionIndex < 0 || !optionValue) return;

          const productData = getDrawerProductData(drawer);
          const selectedOptions = getDrawerSelectedOptions(drawer);
          selectedOptions[optionIndex] = optionValue;
          setDrawerSelectedOptions(drawer, selectedOptions);
          renderMobileDrawerOptions(drawer, productData, selectedOptions);
          setMobileDrawerError(drawer, '');
          return;
        }

        const drawerSubmit = event.target.closest('[data-mobile-drawer-submit]');
        if (drawerSubmit) {
          event.preventDefault();
          addMobileDrawerVariantToCart(drawerSubmit.closest('[data-mobile-product-drawer]'));
          return;
        }

        const drawerClose = event.target.closest('[data-mobile-drawer-close], [data-mobile-drawer-overlay]');
        if (drawerClose) {
          event.preventDefault();
          closeMobileDrawer(drawerClose.closest('[data-mobile-product-drawer]'));
          return;
        }

        const singleAddButton = event.target.closest('[data-hover-add-to-cart]');
        if (singleAddButton) {
          event.preventDefault();
          addHoverVariantToCart(singleAddButton);
          return;
        }

        const sizeButton = event.target.closest('.grid-product__hover-variant-size[data-option-index]');
        if (sizeButton) {
          event.preventDefault();
          handleHoverOptionButton(sizeButton);
        }
      });

      document.addEventListener('keyup', event => {
        if (event.key === 'Escape') {
          closeAllMobileDrawers();
        }
      });

      window.__agent84HoverQuickAddHooked = true;
    }

    // ---- Featured Slider color expansion -------------------------------------------------
    function fcsNormColor(value) {
      return String(value == null ? '' : value).trim().toLowerCase();
    }

    function fcsColorFromTitle(title) {
      const parts = String(title || '').split('-');
      return parts.length ? parts[parts.length - 1].trim() : '';
    }

    function fcsParseColorPairs(raw) {
      return String(raw || '')
        .split(/\r?\n|;/)
        .map(line => line.split('+'))
        .filter(halves => halves.length >= 2)
        .map(halves => [halves[0].trim(), halves[1].trim()])
        .filter(([a, b]) => a && b && fcsNormColor(a) !== fcsNormColor(b));
    }

    function fcsSwatchHtmlToData(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const anchor = tmp.querySelector('a[data-related-product-id]') || tmp.firstElementChild;
      return anchor ? parseProductData(anchor) : null;
    }

    // Parse each style's swatch HTML exactly once into { id, color, data } entries, keyed by
    // style key. Reused for both the related-color universe and the per-card sibling matching.
    function fcsBuildStyleIndex(styleStore) {
      const index = new Map();
      for (const [key, store] of styleStore) {
        const list = [];
        for (const [, html] of store.htmlById) {
          const data = fcsSwatchHtmlToData(html);
          if (!data || !data.id) continue;
          list.push({ id: String(data.id), color: fcsColorFromTitle(data.title), data });
        }
        index.set(key, list);
      }
      return index;
    }

    // Build the ordered, deduped related-color list (port of the Liquid logic):
    //   1. configured pair partner(s) for the anchor color (config order),
    //   2. the anchor color itself,
    //   3. same-group colors (first/last word == anchor group word) — ONLY when no pair matched.
    function fcsBuildRelatedColors(anchorColor, rawPairs, styleIndex) {
      const anchor = String(anchorColor || '').trim();
      const anchorDc = fcsNormColor(anchor);
      const related = [];
      const seen = new Set();

      function add(name) {
        const dc = fcsNormColor(name);
        if (!dc || seen.has(dc)) return;
        seen.add(dc);
        related.push(name);
      }

      let anchorHasPair = false;
      for (const [a, b] of fcsParseColorPairs(rawPairs)) {
        let partner = '';
        if (fcsNormColor(a) === anchorDc) partner = b;
        else if (fcsNormColor(b) === anchorDc) partner = a;
        if (!partner) continue;
        anchorHasPair = true;
        add(partner);
      }

      add(anchor);

      if (!anchorHasPair && anchorDc) {
        const group = anchorDc.split(' ')[0];
        // Color universe = every color the featured styles come in (from the style index).
        const universe = new Map(); // normalized -> original-cased name
        for (const [, list] of styleIndex) {
          for (const entry of list) {
            const dc = fcsNormColor(entry.color);
            if (dc && !universe.has(dc)) universe.set(dc, entry.color);
          }
        }
        for (const [, color] of universe) {
          const words = fcsNormColor(color).split(' ');
          if (words[0] === group || words[words.length - 1] === group) add(color);
        }
      }

      return related;
    }

    // Base product cards. Once Flickity is enabled it moves the cells into .flickity-slider,
    // so when an instance exists we must read its cells rather than the slider's direct children.
    function fcsProductCards(sliderEl, flkty) {
      const cells = flkty && typeof flkty.getCellElements === 'function'
        ? flkty.getCellElements()
        : Array.from(sliderEl.querySelectorAll('.grid__item.grid-product'));
      return cells.filter(card =>
        card.classList.contains('grid-product') &&
        !card.classList.contains('featured-collection-slider__image-slide-item'));
    }

    // A clone copies the base card's scroll-reveal classes (sr-el sr-hidden …), but only the
    // original is registered with scroll-reveal's IntersectionObserver, and its MutationObserver
    // skips anything already marked sr-el. So a clone taken before the visitor scrolled to the
    // slider stays at opacity 0 forever. Strip that state so the clone is picked up as a new item.
    function fcsResetScrollReveal(card) {
      Array.from(card.classList).forEach((cls) => {
        if (cls.indexOf('sr-') === 0) card.classList.remove(cls);
      });
      card.style.removeProperty('--sr-delay');
      if (card.getAttribute('style') === '') card.removeAttribute('style');
    }

    function fcsExpandOne(sliderEl, styleIndex) {
      const anchorColor = sliderEl.getAttribute('data-fcs-anchor-color') || '';
      const anchorId = (sliderEl.getAttribute('data-fcs-anchor-product-id') || '').trim();
      const rawPairs = sliderEl.getAttribute('data-fcs-color-pairs') || '';
      const limit = Number.parseInt(sliderEl.getAttribute('data-fcs-product-limit') || '12', 10) || 12;

      const related = fcsBuildRelatedColors(anchorColor, rawPairs, styleIndex);
      const relatedOrder = new Map();
      related.forEach((name, index) => relatedOrder.set(fcsNormColor(name), index));

      const flkty = window.Flickity && window.Flickity.data(sliderEl);
      const baseCards = fcsProductCards(sliderEl, flkty);
      if (!baseCards.length || !relatedOrder.size) return false;

      const seenIds = new Set();
      if (anchorId) seenIds.add(anchorId);

      const collected = [];

      for (const baseCard of baseCards) {
        if (collected.length >= limit) break;

        const strip = baseCard.querySelector('.productCard-swatches');
        const styleKey = (strip?.getAttribute('data-style-key') || '').trim();
        const list = styleKey ? styleIndex.get(styleKey) : null;
        if (!list || !list.length) continue;

        // Sibling products of this style that fall in a related color, in related-color order.
        const matches = [];
        for (const entry of list) {
          const order = relatedOrder.get(fcsNormColor(entry.color));
          if (order === undefined) continue;
          matches.push({ data: entry.data, order });
        }
        matches.sort((a, b) => a.order - b.order);

        for (const { data } of matches) {
          if (collected.length >= limit) break;
          if (seenIds.has(String(data.id))) continue;
          seenIds.add(String(data.id));

          const clone = baseCard.cloneNode(true);
          fcsResetScrollReveal(clone);
          updateCardContent(clone, data);

          const cloneStrip = clone.querySelector('.productCard-swatches');
          if (cloneStrip) {
            cloneStrip.setAttribute('data-product-id', data.id);
            cloneStrip.setAttribute('data-selected-product-id', data.id);
            if (data.title) cloneStrip.setAttribute('data-product-title', data.title);
            if (data.url) cloneStrip.setAttribute('data-product-url', data.url);
            // Sync the strip's product payload to THIS colour. The clone still carries the base
            // card's variant/option data; without this the quick-add buttons (and any fallback
            // reads) would reference the base colour's variants, so the wrong variant is selected.
            cloneStrip.setAttribute('data-product-price', String(data.price || 0));
            cloneStrip.setAttribute('data-product-compare-price', String(data.comparePrice || 0));
            cloneStrip.setAttribute('data-product-price-varies', String(!!data.priceVaries));
            cloneStrip.setAttribute('data-product-options', JSON.stringify(data.options || []));
            cloneStrip.setAttribute('data-product-variants', JSON.stringify(data.variants || []));
            cloneStrip.setAttribute('data-hover-option-indexes', JSON.stringify(data.hoverOptionIndexes || []));
            cloneStrip.setAttribute('data-size-option-index', String(data.sizeOptionIndex == null ? -1 : data.sizeOptionIndex));
            setSelectedOptions(cloneStrip, []);
            cloneStrip.innerHTML = ''; // swatches re-populated by the next initSwatches pass

            // Rebuild the quick-add controls for this colour's variants (the cloned markup
            // still holds the base card's variant ids until we do).
            renderHoverOptionButtons(cloneStrip, data, []);
          }

          collected.push(clone);
        }
      }

      if (!collected.length) return false;

      if (flkty) {
        flkty.remove(baseCards);
        flkty.append(collected);
        flkty.reloadCells();
        flkty.resize();
      } else {
        baseCards.forEach(card => card.remove());
        collected.forEach(card => sliderEl.appendChild(card));
      }

      const container = sliderEl.closest('.featured-collection-slider-section') || sliderEl.parentElement;
      if (window.theme && typeof theme.reinitProductGridItem === 'function') {
        theme.reinitProductGridItem(container);
      }

      return true;
    }

    function expandFeaturedSliders(styleStore) {
      const sliders = document.querySelectorAll('[data-featured-collection-slider][data-fcs-expand="true"]:not([data-fcs-expanded="true"])');
      if (!sliders.length) return;

      // Parse the swatch store once and share it across every slider on the page.
      const styleIndex = fcsBuildStyleIndex(styleStore);

      for (const sliderEl of sliders) {
        try {
          fcsExpandOne(sliderEl, styleIndex);
        } catch (err) {
          console.error('Featured slider expansion error', err);
        } finally {
          // Always reveal and lock the slider so it is never left hidden or expanded twice.
          sliderEl.setAttribute('data-fcs-expanded', 'true');
          sliderEl.classList.remove('featured-collection-slider--expanding');
        }
      }
    }

    window.initSwatches = async function initSwatches() {
      const containers = Array.from(document.querySelectorAll(SWATCH_CONTAINER_SELECTOR));
      if (!containers.length) return;

      window.__swatchAbort?.abort();
      window.__swatchAbort = new AbortController();
      const signal = window.__swatchAbort.signal;

      const containersByStyle = new Map();
      for (const container of containers) {
        const key = (container.getAttribute('data-style-key') || '').trim();
        if (!key) continue;
        if (!containersByStyle.has(key)) containersByStyle.set(key, []);
        containersByStyle.get(key).push(container);
        container.innerHTML = '';
        // Spinner while the colour catalog loads (styled on the PDP only, see custom.css.liquid).
        container.classList.add('is-loading');
        container.setAttribute('aria-busy', 'true');
      }

      const styleKeys = Array.from(containersByStyle.keys());
      if (!styleKeys.length) return;

      const styleStore = new Map();
      for (const key of styleKeys) {
        styleStore.set(key, { htmlById: new Map() });
      }

      // Publish the uncapped store so other components (e.g. the add-to-cart upsell popup)
      // can read every sibling colour of a style, not just the ones that fit under
      // data-max-swatches. Read-only by convention — nothing outside this file mutates it.
      window.theme = window.theme || {};
      window.theme.styleSwatchStore = styleStore;

      function isUseUrl(container) {
        return (container.getAttribute('data-use-url') || '').toLowerCase() === 'true';
      }

      function showAllSwatches(container) {
        return String(container.getAttribute('data-show-all-swatches') || '').toLowerCase() === 'true';
      }

      function alwaysShowArrow(container) {
        return (container.getAttribute('data-always-show-arrow') || '').toLowerCase() === 'true';
      }

      function getMaxSwatches(container) {
        const attr = isMobileViewport() && container.hasAttribute('data-max-swatches-mobile')
          ? 'data-max-swatches-mobile'
          : 'data-max-swatches';
        const limit = Number.parseInt(container.getAttribute(attr) || '', 10);
        return Number.isFinite(limit) && limit > 0 ? limit : MAX_SWATCHES;
      }

      function replaceMainProductPlaceholders(html, mainProductId) {
        return html
          .replaceAll('<mainProduct>', mainProductId)
          .replaceAll('&lt;mainProduct&gt;', mainProductId);
      }

      function additionalLinkHtml(container) {
        const productUrl = container.getAttribute('data-product-url') || container.getAttribute('data-url') || '';
        if (!productUrl) return '';

        if (alwaysShowArrow(container)) {
          return `
            <a href="${productUrl}" class="productCard-additional" aria-label="View product">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g clip-path="url(#clip0_2273_2839)">
                  <path d="M9 4.5L16.5 12L9 19.5" stroke="#421802" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_2273_2839">
                    <rect width="24" height="24" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </a>
          `;
        }

        return `<a href="${productUrl}" class="productCard-additional">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <g clip-path="url(#clip0_2273_1613)">
              <path d="M3.75 12H20.25" stroke="#421802" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 3.75V20.25" stroke="#421802" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_2273_1613">
                <rect width="24" height="24" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </a>`;
      }

      function buildHtmlForContainer(styleKey, container) {
        const mainProductId = container.getAttribute('data-product-id');
        if (!mainProductId) return { html: '', totalAvailable: 0, shown: 0 };

        const mainId = String(mainProductId);
        const store = styleStore.get(styleKey);
        if (!store) return { html: '', totalAvailable: 0, shown: 0 };

        const useUrl = isUseUrl(container);
        const showAll = showAllSwatches(container);
        const maxSwatches = getMaxSwatches(container);
        const limit = useUrl || showAll ? Infinity : maxSwatches;
        const totalAvailable = store.htmlById.size;

        const sortedAll = Array.from(store.htmlById.keys()).sort((a, b) => {
          const ak = getSortLabelFromSwatchHtml(store.htmlById.get(a) || '');
          const bk = getSortLabelFromSwatchHtml(store.htmlById.get(b) || '');
          const cmp = ak.localeCompare(bk, undefined, { sensitivity: 'base' });
          return cmp !== 0 ? cmp : String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
        });

        // On product cards (collection grid + featured slider — the only containers inside
        // .grid-product) the current product's own colour must lead so it reads as pre-selected
        // and is never pushed past the visible cap. Every other surface (PDP style-swatch row,
        // look-set drawer, look-card popover) keeps pure alphabetical order.
        let ordered = sortedAll;
        const isProductCard = !!container.closest('.grid-product');
        if (isProductCard && store.htmlById.has(mainId)) {
          ordered = [mainId, ...sortedAll.filter((id) => String(id) !== mainId)];
        }

        const ids = (useUrl || showAll) ? ordered : ordered.slice(0, limit);

        let html = '';
        for (const id of ids) {
          const swatchHtml = store.htmlById.get(id);
          if (!swatchHtml) continue;
          html += replaceMainProductPlaceholders(swatchHtml, mainId);
        }

        return { html, totalAvailable, shown: ids.length, limit };
      }

      // PDP Core / Limited A/B layout — the wrapper class comes from product-template-swatches
      // (block setting, or the ?swatch_groups=1|0 preview override).
      function isGroupedContainer(container) {
        return !!container.closest('.product-single__style-swatches--grouped');
      }

      // Keep in sync with the Liquid normalisation in product-template-swatches.liquid.
      function normalizeColorName(value) {
        return String(value || '').toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
      }

      // "Laguna Bra - Black Gem" → "Black Gem"
      function swatchColorName(anchor) {
        const title = anchor.getAttribute('data-product-title') || anchor.getAttribute('title') || '';
        const parts = title.includes(' - ') ? title.split(' - ') : title.split('-');
        return parts[parts.length - 1].trim();
      }

      function renderGroupedContainer(styleKey, container) {
        const mainId = String(container.getAttribute('data-product-id') || '');
        const store = styleStore.get(styleKey);
        if (!mainId || !store) return;

        const coreOrder = parseJsonAttribute(container, 'data-core-colors', []).map(normalizeColorName);
        const labels = parseJsonAttribute(container, 'data-swatch-labels', {});
        const newLabel = container.getAttribute('data-new-label') || '';
        const core = [];
        const limited = [];

        for (const html of store.htmlById.values()) {
          const tpl = document.createElement('template');
          tpl.innerHTML = replaceMainProductPlaceholders(html, mainId).trim();
          const item = tpl.content.firstElementChild;
          const anchor = item?.querySelector('a[data-related-product-id]');
          if (!anchor) continue;

          const name = swatchColorName(anchor);
          const key = normalizeColorName(name);
          // Per-product metafield beats the theme-settings label, which beats the automatic "New".
          const label = anchor.getAttribute('data-swatch-label')
            || labels[key]
            || (anchor.getAttribute('data-new-in') === 'true' ? newLabel : '');

          if (label) {
            // Not a <span>: every span inside .productSwatch is styled as the swatch circle.
            const labelEl = document.createElement('div');
            labelEl.className = 'productSwatch__label';
            labelEl.textContent = label;
            item.appendChild(labelEl);
          }
          if (anchor.getAttribute('data-related-product-id') === mainId) {
            item.classList.add('active');
            anchor.classList.add('active');
            anchor.setAttribute('aria-current', 'true');
          }

          const entry = { item, key, name, coreIndex: coreOrder.indexOf(key) };
          (entry.coreIndex === -1 ? limited : core).push(entry);
        }

        const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        core.sort((a, b) => a.coreIndex - b.coreIndex || byName(a, b));
        limited.sort(byName);

        const groups = [
          { modifier: 'core', heading: container.getAttribute('data-core-heading') || 'Core', entries: core },
          { modifier: 'limited', heading: container.getAttribute('data-limited-heading') || 'Limited', entries: limited }
        ].filter((group) => group.entries.length);

        // Only Core or only Limited → no group heading; the wrapper's single-group class brings
        // back the original option label ("Color") instead.
        const singleGroup = groups.length === 1;
        const wrapper = container.closest('.product-single__style-swatches');
        const optionLabel = wrapper?.querySelector('.product-single__style-swatches__label')?.textContent.trim() || 'Color';

        const fragment = document.createDocumentFragment();
        for (const group of groups) {
          const groupEl = document.createElement('div');
          groupEl.className = `product-single__swatch-group product-single__swatch-group--${group.modifier}`;
          groupEl.setAttribute('role', 'group');
          groupEl.setAttribute('aria-label', singleGroup ? optionLabel : group.heading);
          if (!singleGroup) {
            const headingEl = document.createElement('p');
            headingEl.className = 'product-single__swatch-group__heading';
            headingEl.textContent = group.heading;
            groupEl.appendChild(headingEl);
          }
          const listEl = document.createElement('div');
          listEl.className = 'product-single__swatch-group__list';
          group.entries.forEach((entry) => listEl.appendChild(entry.item));
          groupEl.appendChild(listEl);
          fragment.appendChild(groupEl);
        }

        wrapper?.classList.toggle('product-single__style-swatches--single-group', singleGroup);
        container.classList.add('productCard-swatches--grouped');
        container.replaceChildren(fragment);
      }

      function renderContainer(styleKey, container) {
        if (isGroupedContainer(container)) {
          renderGroupedContainer(styleKey, container);
          return;
        }
        container.classList.remove('productCard-swatches--grouped');
        container.closest('.product-single__style-swatches')?.classList.remove('product-single__style-swatches--single-group');

        const { html, totalAvailable, shown, limit } = buildHtmlForContainer(styleKey, container);
        const useUrl = isUseUrl(container);
        const shouldAddAdditional = alwaysShowArrow(container) || (!useUrl && totalAvailable > limit && shown > 0);

        container.innerHTML = (html || '') + (shouldAddAdditional ? additionalLinkHtml(container) : '');

        const mainId = container.getAttribute('data-product-id');
        if (mainId) {
          const anchor = container.querySelector(`a[data-related-product-id="${CSS.escape(String(mainId))}"]`);
          if (anchor) {
            const swatchItem = anchor.closest('.productSwatch');
            swatchItem?.classList.add('active');
            anchor.classList.add('active');
            // Ordering is decided in buildHtmlForContainer: product cards lead with this
            // (current) colour; all other surfaces stay alphabetical. Here we only flag it active.
          }
        }

        if (useUrl) {
          container.querySelectorAll('a[data-swatch-url]').forEach((anchor) => {
            const url = anchor.getAttribute('data-swatch-url');
            if (url) anchor.href = url;
          });
        }

        const card = container.closest('.grid-product');
        if (card) {
          const activeSwatch = container.querySelector('.productSwatch a.active[data-related-product-id]');
          syncCardDrawerFromProduct(card, parseProductData(activeSwatch || container));
        }
      }

      function ingestPage(doc) {
        const blocks = doc.querySelectorAll('div[data-style-key]');
        if (!blocks.length) return { hadBlocks: false, touchedStyles: new Set() };

        const touchedStyles = new Set();

        for (const block of blocks) {
          const key = (block.getAttribute('data-style-key') || '').trim();
          if (!styleStore.has(key)) continue;

          const store = styleStore.get(key);
          const swatchNodes = block.querySelectorAll('.productSwatch');
          if (!swatchNodes.length) continue;

          for (const node of swatchNodes) {
            const anchor = node.querySelector('a[data-related-product-id]');
            const productId = (anchor?.getAttribute('data-related-product-id') || '').trim();
            if (!productId) continue;

            if (!store.htmlById.has(productId)) {
              store.htmlById.set(productId, node.outerHTML);
              touchedStyles.add(key);
            }
          }
        }

        return { hadBlocks: true, touchedStyles };
      }

      try {
        let page = 1;

        while (true) {
          if (signal.aborted) return;

          // Reuse already-fetched pages. initSwatches re-runs whenever .productCard-swatches
          // nodes are added (e.g. after the Featured Slider injects its color clones); without
          // this cache each re-run would re-download the entire paginated swatch view.
          let htmlText = swatchPageCache.get(page);
          if (htmlText === undefined) {
            const res = await fetch(`${SWATCH_VIEW_BASE_URL}&page=${page}`, {
              method: 'GET',
              credentials: 'same-origin',
              headers: { 'X-Requested-With': 'XMLHttpRequest' },
              signal
            });

            if (!res.ok) break;

            htmlText = await res.text();
            swatchPageCache.set(page, htmlText);
          }
          if (!htmlText || htmlText.length < 50) break;

          const doc = new DOMParser().parseFromString(htmlText, 'text/html');
          const { hadBlocks, touchedStyles } = ingestPage(doc);
          if (!hadBlocks) break;

          for (const styleKey of touchedStyles) {
            const list = containersByStyle.get(styleKey) || [];
            for (const container of list) {
              renderContainer(styleKey, container);
            }
          }

          if (touchedStyles.size) {
            document.dispatchEvent(new CustomEvent('styleSwatches:updated', {
              detail: { keys: Array.from(touchedStyles) }
            }));
          }

          page++;
        }
      } catch (err) {
        if (err?.name !== 'AbortError') console.error(err);
      } finally {
        // An aborted run hands its containers to the newer initSwatches, which clears them.
        if (!signal.aborted) {
          for (const container of containers) {
            container.classList.remove('is-loading');
            container.removeAttribute('aria-busy');
          }
        }
      }

      // Swatch-driven color expansion for the Featured Slider on product pages.
      // styleStore now holds every sibling-color product for each style key on the page
      // (gathered via the paginated ?view=swatches fetch, bypassing the all_products 20/page cap).
      if (!signal.aborted) {
        try {
          expandFeaturedSliders(styleStore);
        } catch (err) {
          console.error('Featured slider color expansion failed', err);
        }
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', debounceInit);
    } else {
      debounceInit();
    }

    // Fail-safe: if the swatch fetch hangs/fails and expansion never runs, never leave the
    // Featured Slider stuck in its loading-skeleton state — reveal the server-rendered cards.
    window.setTimeout(function () {
      document.querySelectorAll('[data-featured-collection-slider].featured-collection-slider--expanding')
        .forEach(function (el) { el.classList.remove('featured-collection-slider--expanding'); });
    }, 12000);
  })();
